import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';
import LanguageDetector from 'i18next-browser-languagedetector';

import zh from './locales/zh.json';
import en from './locales/en.json';
import ru from './locales/ru.json';
import uk from './locales/uk.json';
import fr from './locales/fr.json';
import es from './locales/es.json';
import pt from './locales/pt.json';
import ja from './locales/ja.json';
import ko from './locales/ko.json';
import ar from './locales/ar.json';
import uz from './locales/uz.json';

const resources = {
  zh: { translation: zh },
  en: { translation: en },
  ru: { translation: ru },
  uk: { translation: uk },
  fr: { translation: fr },
  es: { translation: es },
  pt: { translation: pt },
  ja: { translation: ja },
  ko: { translation: ko },
  ar: { translation: ar },
  uz: { translation: uz }
};

// 支持的语言列表
const SUPPORTED_LANGUAGES = ['en', 'zh', 'ru', 'uk', 'fr', 'es', 'pt', 'ja', 'ko', 'ar', 'uz'];

// 非中英语言列表（用于兼容性导出）
const AUTO_TRANSLATE_LANGS = ['ru', 'uk', 'fr', 'es', 'pt', 'ja', 'ko', 'ar', 'uz'];

// 检查是否需要自动翻译（保留用于兼容性，翻译已在构建时完成）
export const needsAutoTranslation = (lang) => AUTO_TRANSLATE_LANGS.includes(lang);

// 清除缓存（保留用于兼容性）
export const clearTranslationCache = () => {};

// 预翻译（保留用于兼容性，翻译已在构建时完成）
export const preTranslateLanguage = async (targetLang) => {
  console.log(`[i18n] Language ${targetLang} translations loaded from pre-built files`);
};

// 自动翻译文本（保留用于兼容性，翻译已在构建时完成）
export const autoTranslateText = (text) => text;

// 支持的语言映射：将浏览器语言代码映射到应用支持的语言
const languageMapping = {
  // 中文变体
  'zh': 'zh', 'zh-cn': 'zh', 'zh-tw': 'zh', 'zh-hk': 'zh', 'zh-sg': 'zh',
  'zh-hans': 'zh', 'zh-hant': 'zh', 'cmn': 'zh', 'wuu': 'zh', 'yue': 'zh',
  // 英文变体
  'en': 'en', 'en-us': 'en', 'en-gb': 'en', 'en-au': 'en', 'en-ca': 'en',
  'en-nz': 'en', 'en-ie': 'en', 'en-za': 'en', 'en-in': 'en', 'en-sg': 'en',
  // 俄文变体
  'ru': 'ru', 'ru-ru': 'ru', 'ru-by': 'ru', 'ru-kz': 'ru', 'ru-ua': 'ru',
  // 乌克兰语
  'uk': 'uk', 'uk-ua': 'uk', 'ukr': 'uk',
  // 法语变体
  'fr': 'fr', 'fr-fr': 'fr', 'fr-be': 'fr', 'fr-ca': 'fr', 'fr-ch': 'fr', 'fra': 'fr',
  // 西班牙语变体
  'es': 'es', 'es-es': 'es', 'es-mx': 'es', 'es-ar': 'es', 'es-co': 'es', 'spa': 'es',
  // 葡萄牙语变体
  'pt': 'pt', 'pt-pt': 'pt', 'pt-br': 'pt', 'por': 'pt',
  // 日语
  'ja': 'ja', 'ja-jp': 'ja', 'jpn': 'ja',
  // 韩语
  'ko': 'ko', 'ko-kr': 'ko', 'kor': 'ko',
  // 阿拉伯语变体
  'ar': 'ar', 'ar-sa': 'ar', 'ar-ae': 'ar', 'ar-eg': 'ar', 'ara': 'ar',
  // 乌兹别克语
  'uz': 'uz', 'uz-uz': 'uz', 'uzb': 'uz',
};

/**
 * 将检测到的语言转换为支持的语言代码
 */
const convertLanguage = (lng) => {
  if (!lng) return 'en';
  const normalizedLng = lng.toLowerCase().replace(/_/g, '-');
  
  if (languageMapping[normalizedLng]) {
    return languageMapping[normalizedLng];
  }
  
  const prefix = normalizedLng.split('-')[0];
  if (SUPPORTED_LANGUAGES.includes(prefix)) {
    return prefix;
  }
  if (languageMapping[prefix]) {
    return languageMapping[prefix];
  }
  
  return 'en';
};

/**
 * 获取浏览器偏好语言
 */
const getBrowserPreferredLanguage = () => {
  const browserLanguages = navigator.languages || [navigator.language];
  for (const lang of browserLanguages) {
    const converted = convertLanguage(lang);
    if (SUPPORTED_LANGUAGES.includes(converted)) {
      return converted;
    }
  }
  return 'zh';
};

i18n
  .use(LanguageDetector)
  .use(initReactI18next)
  .init({
    resources,
    fallbackLng: 'en',
    supportedLngs: SUPPORTED_LANGUAGES,
    nonExplicitSupportedLngs: false,
    load: 'currentOnly',
    interpolation: {
      escapeValue: false
    },
    detection: {
      order: ['querystring', 'localStorage', 'sessionStorage', 'navigator', 'htmlTag'],
      caches: ['localStorage', 'sessionStorage'],
      lookupQuerystring: 'lng',
      lookupLocalStorage: 'i18nextLng',
      lookupSessionStorage: 'i18nextLng',
      htmlTag: document.documentElement,
      convertDetectedLanguage: convertLanguage,
      checkWhitelist: true
    },
    debug: process.env.NODE_ENV === 'development'
  });

// 监听语言变化
i18n.on('languageChanged', (lng) => {
  document.documentElement.setAttribute('lang', lng);
  document.documentElement.setAttribute('dir', lng === 'ar' ? 'rtl' : 'ltr');
});

// 初始化时设置 HTML lang 属性
if (i18n.language) {
  document.documentElement.setAttribute('lang', i18n.language);
  document.documentElement.setAttribute('dir', i18n.language === 'ar' ? 'rtl' : 'ltr');
}

// 导出
export const getAppLanguage = convertLanguage;
export const getBrowserLanguage = getBrowserPreferredLanguage;
export const supportedLanguages = SUPPORTED_LANGUAGES;

export default i18n;
